// Многоезичност (BG / EN).
//
// Принцип: българският текст в кода Е ключът. t('Вход') връща 'Log in' на
// английски и 'Вход' на български (или ако липсва превод). Така кодът остава
// четим, а липсващ превод никога не чупи екрана.
//
// Как се избира езикът (в този ред):
//   1. Изричен избор на потребителя (Профил → Език), пазен в AsyncStorage.
//   2. Езикът на телефона/браузъра е български → bg.
//   3. Часовата зона е Europe/Sofia → bg (телефон на английски, но човекът е
//      в България — най-честият случай).
//   4. Регионът на телефона е BG (напр. en-BG) → bg.
//   5. Държава по IP (бекендът GET /geo/country) → BG → bg, друга → en.
//   6. Неизвестно (без мрежа) → bg.
//
// Смяна на езика в движение: App.tsx слуша useLocale() и монтира наново
// дървото (key={locale}), така че всеки екран се преведе веднага.

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSyncExternalStore } from 'react';
import { NativeModules, Platform } from 'react-native';
import { EN } from './en';

export type Locale = 'bg' | 'en';
export type LocaleChoice = Locale | 'auto';

export const LOCALES: { code: Locale; label: string }[] = [
  { code: 'bg', label: 'Български' },
  { code: 'en', label: 'English' },
];

const STORAGE_KEY = 'livupp.locale';

let locale: Locale = 'bg';
let choice: LocaleChoice = 'auto';
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((fn) => fn());
}

export function getLocale(): Locale {
  return locale;
}

export function getLocaleChoice(): LocaleChoice {
  return choice;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Хук за компоненти, които трябва да се преизчислят при смяна на езика
// (App.tsx). Обикновените екрани не го ползват — App ги монтира наново.
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

// Превод с параметри: t('Здравей, {name}', { name }) → 'Hi, Ivan'.
export function t(text: string, params?: Record<string, string | number>): string {
  let out = locale === 'en' ? (EN[text] ?? text) : text;
  if (params) {
    for (const [k, v] of Object.entries(params)) out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

// Езиците на устройството, без допълнителни native модули (за да върви и по OTA):
// уеб → navigator.languages; iOS → SettingsManager; Android → I18nManager; иначе Intl.
function deviceLanguages(): string[] {
  const list: string[] = [];
  if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
    list.push(...((navigator.languages as string[] | undefined) ?? []), navigator.language);
  } else {
    const sm = (NativeModules as any)?.SettingsManager?.settings;
    if (Array.isArray(sm?.AppleLanguages)) list.push(...sm.AppleLanguages);
    if (typeof sm?.AppleLocale === 'string') list.push(sm.AppleLocale);
    const li = (NativeModules as any)?.I18nManager?.localeIdentifier;
    if (typeof li === 'string') list.push(li);
  }
  try {
    list.push(Intl.DateTimeFormat().resolvedOptions().locale);
  } catch {
    /* Intl може да липсва на стар двигател */
  }
  return list.filter((l): l is string => typeof l === 'string' && l.length > 0).map((l) => l.replace('_', '-'));
}

function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

// Държава по IP през нашия бекенд. Кратък таймаут — не бавим стартирането.
async function ipCountry(timeoutMs: number): Promise<string | null> {
  try {
    // късен require — api.ts зависи от i18n за Accept-Language
    const { API_URL } = require('../api') as { API_URL: string };
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl?.abort(), timeoutMs);
    const res = await fetch(`${API_URL}/geo/country`, { signal: ctrl?.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const json = (await res.json()) as { country: string | null };
    return json.country ?? null;
  } catch {
    return null;
  }
}

export async function detectLocale(): Promise<Locale> {
  const langs = deviceLanguages();
  if (langs.some((l) => /^bg/i.test(l))) return 'bg';
  if (deviceTimeZone() === 'Europe/Sofia') return 'bg';
  if (langs.some((l) => /-BG$/i.test(l))) return 'bg';
  const country = await ipCountry(1500);
  if (country === 'BG' || country === null) return 'bg';
  return 'en';
}

// Извиква се веднъж при старт (преди първия екран).
export async function initLocale(): Promise<Locale> {
  try {
    const saved = (await AsyncStorage.getItem(STORAGE_KEY)) as LocaleChoice | null;
    if (saved === 'bg' || saved === 'en') {
      choice = saved;
      locale = saved;
    } else {
      choice = 'auto';
      locale = await detectLocale();
    }
  } catch {
    locale = 'bg';
  }
  emit();
  return locale;
}

export async function setLocaleChoice(next: LocaleChoice): Promise<void> {
  choice = next;
  try {
    await AsyncStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* без памет — важи до рестарт */
  }
  locale = next === 'auto' ? await detectLocale() : next;
  emit();
}

// Кратък код за Accept-Language към API-то (сървърът превежда грешки,
// известия, SMS и имейли според него).
export function acceptLanguage(): string {
  return locale === 'en' ? 'en, bg;q=0.5' : 'bg, en;q=0.5';
}
